import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Icon from '../components/brand/Icon';
import './PricingPage.css';

const HowItWorks = () => (
  <div className="pricing-how-section container">
    <h2>How RGV Smart Pay Works</h2>
    <p className="pricing-how-sub">
      You set your parking fee. Customers pay online and get a permit instantly.
      You receive your full parking fee. We charge the customer a small service fee per transaction.
    </p>
    <div className="pricing-how-grid">
      <div className="pricing-how-step">
        <div className="pricing-how-num">1</div>
        <Icon name="qr" size={28} />
        <h4>You print a QR sign</h4>
        <p>We generate a custom QR code for your lot. Print and post it.</p>
      </div>
      <div className="pricing-how-step">
        <div className="pricing-how-num">2</div>
        <Icon name="card" size={28} />
        <h4>Customers scan and pay</h4>
        <p>They enter their plate, pay by card, and get an instant permit ID.</p>
      </div>
      <div className="pricing-how-step">
        <div className="pricing-how-num">3</div>
        <Icon name="dollar" size={28} />
        <h4>You get paid</h4>
        <p>Your full parking fee deposits to your bank account. No monthly fee. No cut.</p>
      </div>
      <div className="pricing-how-step">
        <div className="pricing-how-num">4</div>
        <Icon name="search" size={28} />
        <h4>You enforce</h4>
        <p>Look up any plate in seconds to confirm payment before ticketing.</p>
      </div>
    </div>
  </div>
);

const FeeExample = () => (
  <div className="pricing-example-section container">
    <h2>Example transaction</h2>
    <p className="pricing-how-sub">Using a $5 parking permit as an example.</p>
    <div className="pricing-example-box">
      <div className="pricing-example-row">
        <span>Parking amount <span className="pricing-example-note">(your price, you set this)</span></span>
        <span className="pricing-example-val">$5.00</span>
      </div>
      <div className="pricing-example-row pricing-example-fee">
        <span>Convenience fee <span className="pricing-example-note">(added at checkout, charged to customer)</span></span>
        <span className="pricing-example-val">$1.00</span>
      </div>
      <div className="pricing-example-divider" />
      <div className="pricing-example-row pricing-example-total">
        <span>Customer pays</span>
        <span className="pricing-example-val">$6.00</span>
      </div>
      <div className="pricing-example-row pricing-example-owner">
        <span><strong>Business receives</strong></span>
        <span className="pricing-example-val pricing-example-green"><strong>$5.00</strong></span>
      </div>
    </div>
    <p className="pricing-example-footer">
      You always receive your full parking amount. The convenience fee is paid by the customer and covers card processing costs.
    </p>
    <p className="pricing-example-footer">
      Tax rules vary by city and business type. Parking lot owners should confirm tax handling with their accountant or local tax authority.
    </p>
  </div>
);

const PricingPage = () => {
  return (
    <div className="pricing-page">
      <Navbar />
      <div className="pricing-hero">
        <div className="container">
          <div className="pricing-hero-badge">For Parking Lot Owners</div>
          <h1>Free to Set Up. No Monthly Fees.</h1>
          <p>
            RGV Smart Pay is free for lot owners. Customers pay a small service fee per transaction.
            You keep your full parking fee, every time.
          </p>
          <Link to="/business-signup" className="btn btn-white-outline pricing-hero-cta">
            Get Started Free
          </Link>
        </div>
      </div>

      <HowItWorks />

      <FeeExample />

      <div className="pricing-includes-section container">
        <h2>Everything included, at no cost to you</h2>
        <div className="pricing-includes-grid">
          {[
            { icon: 'qr', title: 'Custom QR signs', desc: 'Printable signs for your lot. Ready in minutes.' },
            { icon: 'card', title: 'Online payments', desc: 'Customers pay by credit or debit card on any device.' },
            { icon: 'search', title: 'Enforcement lookup', desc: 'Look up any plate instantly before writing a ticket.' },
            { icon: 'shield', title: 'Fraud protection', desc: 'Stripe handles card security and dispute protection.' },
            { icon: 'doc', title: 'Permit records', desc: 'Every transaction is logged with plate, date, and time.' },
            { icon: 'dollar', title: 'Fast payouts', desc: 'Funds deposit to your linked bank within 2 business days.' },
          ].map((item) => (
            <div className="pricing-include-card" key={item.title}>
              <div className="pricing-include-icon"><Icon name={item.icon} size={24} /></div>
              <h4>{item.title}</h4>
              <p>{item.desc}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="pricing-faq container">
        <h2>Questions from lot owners</h2>
        <div className="faq-grid">
          {[
            {
              q: 'Is there a monthly fee or setup fee?',
              a: 'No. RGV Smart Pay is completely free for lot owners. There are no monthly fees, no setup fees, and no contracts.',
            },
            {
              q: 'Who pays the convenience fee?',
              a: 'The customer pays a $1.00 convenience fee when they buy a permit. Card processing costs come out of that fee. You receive your full parking amount.',
            },
            {
              q: 'Do I need to collect sales tax?',
              a: 'Tax rules vary by city and business type. Parking lot owners should confirm tax handling with their accountant or local tax authority. RGV Smart Pay does not collect or remit taxes on your behalf.',
            },
            {
              q: 'How do I know if someone paid?',
              a: 'Use the enforcement lookup to check any plate number. It shows paid, validated, or unpaid status instantly.',
            },
            {
              q: 'How do customers prove they paid?',
              a: 'After paying, customers receive a permit ID on screen. They can screenshot it or provide their email for a receipt. Enforcement staff can verify any permit ID.',
            },
            {
              q: 'When do I get paid?',
              a: 'Payments deposit to your linked bank account within 2 business days through Stripe.',
            },
          ].map((item) => (
            <div className="faq-item" key={item.q}>
              <h4>{item.q}</h4>
              <p>{item.a}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="pricing-cta-section">
        <div className="container">
          <h2>Ready to protect your lot?</h2>
          <p>Set up takes less than 10 minutes. No fee. No contract.</p>
          <div className="pricing-cta-actions">
            <Link to="/business-signup" className="btn btn-primary btn-lg">Get Started Free</Link>
            <Link to="/contact" className="btn btn-outline btn-lg">Talk to Us First</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
